"use client";

import * as SliderPrimitive from "@radix-ui/react-slider";
import type { ReactNode } from "react";
import { useId, useRef, useState } from "react";
import {
  BarChart,
  type BarChartDataPoint,
} from "../../elements/bar-chart/bar-chart";
import { Button } from "../../elements/button/button";
import { Input } from "../../elements/input/input";
import {
  RadioGroup,
  type RadioGroupOption,
} from "../../elements/radio-group/radio-group";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";

export type PriceFilterMicrocopies = {
  title: string;
  resetLabel: string;
  currencySymbol: string;
  minLabel: string;
  maxLabel: string;
  okButtonLabel: string;
  priceTypeLabel: string;
  dealerPriceLabel: string;
  retailPriceLabel: string;
};

export type PriceType = "dealer" | "retail";

export type PriceFilterProps = {
  min: number;
  max: number;
  value: [number, number];
  defaultValue?: [number, number];
  step?: number;
  distribution?: BarChartDataPoint[];
  selectedPriceType?: PriceType;
  microcopies: PriceFilterMicrocopies;
  onValueChange?: (value: [number, number]) => void;
  onFilterAction?: (value: [number, number]) => void;
  onPriceTypeChange?: (value: PriceType) => void;
  onReset?: () => void;
  className?: string;
};

type PriceInputsState = {
  minInput: string;
  maxInput: string;
};

type FocusedPriceInput = "min" | "max" | null;

type PriceFilterState = {
  draftVersion: number;
  focusedInput: FocusedPriceInput;
};

// useLatest pattern to avoid useEffect for keeping latest prop values in callbacks.
function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getNumericValue(input: string, fallback: number): number {
  const normalizedInput = input.replace(/[^\d-]/g, "");

  if (normalizedInput === "" || normalizedInput === "-") {
    return fallback;
  }

  const parsedValue = Number(normalizedInput);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function sanitizeNumericInput(input: string): string {
  let normalizedValue = "";

  for (const currentChar of input) {
    if (/\d/.test(currentChar)) {
      normalizedValue += currentChar;
      continue;
    }

    if (currentChar === "-" && normalizedValue.length === 0) {
      normalizedValue += currentChar;
    }
  }

  return normalizedValue;
}

function formatCurrencyInput(value: number, currencySymbol: string): string {
  const formattedValue = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);

  return `${currencySymbol} ${formattedValue}`;
}

function toActiveRange(
  min: number,
  max: number,
  value: [number, number],
  bucketCount: number
): [number, number] {
  const ratio = max - min || 1;
  const start = Math.floor(((value[0] - min) / ratio) * bucketCount);
  const end = Math.ceil(((value[1] - min) / ratio) * bucketCount) - 1;
  return [Math.max(0, start), Math.min(bucketCount - 1, end)];
}

export function PriceFilter(props: Readonly<PriceFilterProps>): ReactNode {
  const priceTypeGroupName = useId();
  const minInputId = useId();
  const maxInputId = useId();
  const step = props.step ?? 1;
  const latestValueRef = useLatest(props.value);
  const [state, setState] = useState<PriceFilterState>({
    draftVersion: 0,
    focusedInput: null,
  });
  const draftInputsRef = useRef<PriceInputsState>({
    minInput: String(props.value[0]),
    maxInput: String(props.value[1]),
  });
  const lastSyncedValueRef = useRef<[number, number]>(props.value);
  const selectedPriceType = props.selectedPriceType ?? "dealer";
  const priceTypeOptions: RadioGroupOption[] = [
    {
      value: "dealer",
      label: props.microcopies.dealerPriceLabel,
    },
    {
      value: "retail",
      label: props.microcopies.retailPriceLabel,
    },
  ];

  const activeRange =
    props.distribution && props.distribution.length > 0
      ? toActiveRange(
          props.min,
          props.max,
          props.value,
          props.distribution.length
        )
      : undefined;

  if (
    lastSyncedValueRef.current[0] !== props.value[0] ||
    lastSyncedValueRef.current[1] !== props.value[1]
  ) {
    lastSyncedValueRef.current = props.value;
    draftInputsRef.current = {
      minInput: String(props.value[0]),
      maxInput: String(props.value[1]),
    };
  }

  function markDraftUpdated() {
    setState((currentState) => ({
      ...currentState,
      draftVersion: currentState.draftVersion + 1,
    }));
  }

  function setFocusedInput(focusedInput: FocusedPriceInput) {
    setState((currentState) => ({
      ...currentState,
      focusedInput,
    }));
  }

  function applyPriceFilter() {
    const nextValue = getDraftRange();

    props.onValueChange?.(nextValue);
    props.onFilterAction?.(nextValue);
  }

  function getDraftRange(): [number, number] {
    const parsedMin = getNumericValue(
      draftInputsRef.current.minInput,
      latestValueRef.current[0]
    );
    const parsedMax = getNumericValue(
      draftInputsRef.current.maxInput,
      latestValueRef.current[1]
    );
    const clampedMin = clamp(parsedMin, props.min, props.max);
    const clampedMax = clamp(parsedMax, props.min, props.max);

    return clampedMin <= clampedMax
      ? [clampedMin, clampedMax]
      : [clampedMax, clampedMin];
  }

  const draftRange = getDraftRange();
  const hasUnappliedChanges =
    draftRange[0] !== props.value[0] || draftRange[1] !== props.value[1];
  const minInputValue =
    state.focusedInput === "min"
      ? draftInputsRef.current.minInput
      : formatCurrencyInput(
          getNumericValue(draftInputsRef.current.minInput, props.value[0]),
          props.microcopies.currencySymbol
        );
  const maxInputValue =
    state.focusedInput === "max"
      ? draftInputsRef.current.maxInput
      : formatCurrencyInput(
          getNumericValue(draftInputsRef.current.maxInput, props.value[1]),
          props.microcopies.currencySymbol
        );

  return (
    <div className={cn("space-y-3", props.className)}>
      <fieldset className="space-y-2">
        <legend>
          <Text variant="body-sm">{props.microcopies.priceTypeLabel}</Text>
        </legend>
        <RadioGroup
          className="gap-1"
          labelVariant="body-sm"
          name={priceTypeGroupName}
          onValueChange={(value) => {
            if (value === "dealer" || value === "retail") {
              props.onPriceTypeChange?.(value);
            }
          }}
          options={priceTypeOptions}
          value={selectedPriceType}
        />
      </fieldset>

      {/* Histogram */}
      {props.distribution ? (
        <BarChart
          activeRange={activeRange}
          data={props.distribution}
          height={48}
        />
      ) : null}

      {/* Dual-handle slider */}
      <SliderPrimitive.Root
        className="relative flex w-full touch-none select-none items-center"
        max={props.max}
        min={props.min}
        onValueChange={(vals: [number, number]) => {
          draftInputsRef.current = {
            minInput: String(vals[0]),
            maxInput: String(vals[1]),
          };
          markDraftUpdated();
        }}
        step={step}
        value={draftRange}
      >
        <SliderPrimitive.Track className="relative h-2.5 w-full grow overflow-hidden rounded-full bg-stone-200">
          <SliderPrimitive.Range className="absolute h-full bg-stone-900" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label={`${props.microcopies.title} minimum price`}
          className="block h-4 w-4 rounded-full border border-stone-900 bg-white shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        />
        <SliderPrimitive.Thumb
          aria-label={`${props.microcopies.title} maximum price`}
          className="block h-4 w-4 rounded-full border border-stone-900 bg-white shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        />
      </SliderPrimitive.Root>

      {/* Current price range display */}
      <div className="flex items-center justify-between">
        <Text variant="body-sm">
          {`${props.microcopies.currencySymbol} ${props.value[0]}`}
        </Text>
        <Text variant="body-sm">
          {`${props.microcopies.currencySymbol} ${props.value[1]}`}
        </Text>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
          <div className="space-y-1">
            <label className="block" htmlFor={minInputId}>
              <Text variant="body-sm-bold">{props.microcopies.minLabel}</Text>
            </label>
            <Input
              id={minInputId}
              inputMode="numeric"
              max={props.max}
              min={props.min}
              onBlur={() => {
                setFocusedInput(null);
              }}
              onChange={(event) => {
                const normalizedInput = sanitizeNumericInput(
                  event.target.value
                );
                draftInputsRef.current = {
                  ...draftInputsRef.current,
                  minInput: normalizedInput,
                };
                markDraftUpdated();
              }}
              onFocus={() => {
                setFocusedInput("min");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  applyPriceFilter();
                }
              }}
              step={step}
              type="text"
              value={minInputValue}
            />
          </div>

          <span className="pb-3">-</span>

          <div className="space-y-1">
            <label className="block" htmlFor={maxInputId}>
              <Text variant="body-sm-bold">{props.microcopies.maxLabel}</Text>
            </label>
            <Input
              id={maxInputId}
              inputMode="numeric"
              max={props.max}
              min={props.min}
              onBlur={() => {
                setFocusedInput(null);
              }}
              onChange={(event) => {
                const normalizedInput = sanitizeNumericInput(
                  event.target.value
                );
                draftInputsRef.current = {
                  ...draftInputsRef.current,
                  maxInput: normalizedInput,
                };
                markDraftUpdated();
              }}
              onFocus={() => {
                setFocusedInput("max");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  applyPriceFilter();
                }
              }}
              step={step}
              type="text"
              value={maxInputValue}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            disabled={!hasUnappliedChanges}
            onClick={applyPriceFilter}
            size="sm"
            type="button"
            variant="outline-no-hover"
          >
            {props.microcopies.okButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
